// ─── Granular Permissions admin UI ─────────────────────────────────────────
// Drill-down editor for the permission_catalog / role_permission_grants /
// user_permission_grants system (server/routes/permissions.js). Two modes:
// edit a role's template ("group permissions"), or one user's individual
// overrides — mirrors the Django-admin reference shape this was modeled on.
// Every module the catalog knows about shows up here automatically (today:
// the 17 legacy modules at module-level only, plus 'dashboard' widgets and
// 'employees' tabs/fields in full) — extending the catalog to a new module
// later needs no change to this component.
import React, { useEffect, useMemo, useState } from "react";
import { Search, ChevronDown, ChevronRight, RotateCcw, Check, Info, X } from "lucide-react";
import api from "../api";
import { Bt, Cd, Sl, SearchSelect } from "../components";
import { notify, confirmDialog } from "../notify";

const ROLE_LABELS = { hr_head: "HR Head", centre_head: "Centre Head", centre_hr: "Centre HR", manager: "Manager", team_leader: "Team Leader", team_incharge: "Team Incharge", recruiter: "Recruiter", accounts: "Accounts", employee: "Employee" };
const EDITABLE_ROLES = Object.keys(ROLE_LABELS);
const MODULE_LABELS = { dashboard: "Dashboard", myprofile: "My Profile", attendance: "Attendance", leaves: "Leaves", appraisal: "Appraisal", payroll: "Payroll", expenses: "Expenses", assets: "Assets", helpdesk: "Helpdesk", documents: "Policies", separation: "Offboarding", employees: "Employees", org: "Organization", onboarding: "Onboarding", recruitment: "Recruitment", compliance: "Compliance", settings: "Settings", hrcorner: "HR Corner", templates: "Email/Form Templates", chat: "Chat", reports: "Reports & Analytics" };
const ACTIONS = ["view", "edit", "delete", "approve", "reject"];

// ── "i" help: a screenshot of the screen this permission controls, with the affected
// part boxed in red. Images live in public/perm-help/<module>/<tab>__<field>.jpg and are
// listed in public/perm-help/manifest.json ({ "<module>/<tab>__<field>": { notes: [...] } }).
const slug = (v) => (v ? String(v).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "") || "_" : "_");
const helpKey = (n) => `${n.module}/${slug(n.tab)}__${slug(n.field)}`;
const HELP_BASE = (process.env.PUBLIC_URL || "") + "/perm-help/";
let helpManifest = null;
const loadHelpManifest = () => {
  if (!helpManifest) helpManifest = fetch(HELP_BASE + "manifest.json").then((r) => (r.ok ? r.json() : {})).catch(() => ({}));
  return helpManifest;
};

const PermInfo = ({ node }) => {
  const [man, setMan] = useState(null);
  const [hover, setHover] = useState(null); // { top, left }
  const [full, setFull] = useState(false);
  useEffect(() => { let live = true; loadHelpManifest().then((m) => live && setMan(m)); return () => { live = false; }; }, []);
  useEffect(() => {
    if (!full) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setFull(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [full]);
  const key = helpKey(node);
  const entry = man && man[key];
  const src = HELP_BASE + key + ".jpg";
  const show = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const w = 380;
    const left = Math.max(8, Math.min(r.left, window.innerWidth - w - 8));
    const top = r.bottom + 6 + 300 > window.innerHeight ? Math.max(8, r.top - 306) : r.bottom + 6;
    setHover({ top, left });
  };
  return (
    <>
      <button
        type="button" aria-label="What does this permission control?"
        onMouseEnter={show} onMouseLeave={() => setHover(null)} onFocus={show} onBlur={() => setHover(null)}
        onClick={(e) => { e.stopPropagation(); if (entry) { setHover(null); setFull(true); } }}
        className={`inline-flex items-center justify-center w-[18px] h-[18px] ml-[6px] rounded-full border-0 bg-transparent p-0 align-middle flex-shrink-0 ${entry ? "text-p cursor-pointer hover:bg-pg" : "text-t3 opacity-50 cursor-default"}`}
      >
        <Info size={14} />
      </button>
      {hover && (
        <div style={{ position: "fixed", top: hover.top, left: hover.left, width: 380 }} className="z-[600] bg-bg1 border border-b0 rounded-[10px] shadow-[0_12px_32px_rgba(15,23,42,0.25)] p-2 pointer-events-none whitespace-normal normal-case">
          {entry ? (
            <>
              <img src={src} alt="" className="w-full rounded-[6px] border border-b0" />
              {entry.notes && entry.notes.length > 0 && <div className="mt-[6px] text-[11.5px] text-t2">{entry.notes.map((t, i) => <div key={i}>{t}</div>)}</div>}
              <div className="mt-[4px] text-[11px] text-t3">Click to enlarge</div>
            </>
          ) : (
            <div className="px-2 py-3 text-[12px] text-t3">No screenshot for this permission yet.</div>
          )}
        </div>
      )}
      {full && entry && (
        <div className="fixed inset-0 z-[700] bg-black/85 flex flex-col items-center justify-center p-4 whitespace-normal" onClick={() => setFull(false)}>
          <button type="button" aria-label="Close" onClick={() => setFull(false)} className="absolute top-4 right-4 w-[34px] h-[34px] rounded-full bg-white/15 text-white border-0 cursor-pointer flex items-center justify-center hover:bg-white/25"><X size={18} /></button>
          <img src={src} alt="" className="max-w-[96vw] max-h-[86vh] rounded-[8px] shadow-2xl" onClick={(e) => e.stopPropagation()} />
          {entry.notes && entry.notes.length > 0 && <div className="mt-3 text-[13px] text-white/90 text-center" onClick={(e) => e.stopPropagation()}>{entry.notes.map((t, i) => <div key={i}>{t}</div>)}</div>}
        </div>
      )}
    </>
  );
};

const ACTION_LABELS = { view: "View", edit: "Edit", delete: "Delete", approve: "Approve", reject: "Reject" };

// Flat catalog rows -> one node per (module,tab,field), actions merged.
function pivot(catalog) {
  const map = new Map();
  for (const row of catalog) {
    const key = `${row.module}|${row.tab || ""}|${row.field || ""}`;
    if (!map.has(key)) map.set(key, { module: row.module, tab: row.tab, field: row.field, label: row.label, actions: {} });
    map.get(key).actions[row.action] = row.id;
  }
  return [...map.values()];
}

const fieldLabel = (n) => n.label.replace(/^.*—\s*/, "").replace(/\s*\((View|Edit|Delete|Approve)\)$/, "");

// `descendants` (optional): other catalog nodes this one is a parent of —
// a module node's descendants are every tab/field under it, a tab node's
// descendants are its own fields. `ancestors` (optional): nodes above this
// one (a field's tab and module; a tab's module). Toggling a checkbox
// cascades DOWN on uncheck (turning off "Dashboard" View turns off every
// widget's View too) and UP on check (turning on one widget's Edit also
// turns on the Dashboard module's Edit) — so a parent being off always
// means nothing under it is on, kept true through normal use rather than
// enforced separately at save time. A leaf field has no descendants; a
// module node has no ancestors.
const SCOPE_OPTIONS = [
  { v: "all", l: "All (company-wide)" },
  { v: "centre", l: "Centre-wise (own office/company)" },
  { v: "team", l: "Team (own reports only)" },
];

// Who "Apply to Users" (role-template mode) overwrites. 'all' and
// 'customized' deliberately discard individual overrides — see the confirm
// copy in applyToUsers() below, which spells that out per option.
const APPLY_TARGET_OPTIONS = [
  { v: "none", l: "Just save — don't re-apply to anyone", d: "People on this role already receive newly added permissions; individual customizations are kept." },
  { v: "non_customized", l: "Non-customized users only", d: "Re-applies the template to everyone on this role who has no individual customization." },
  { v: "all", l: "All users (incl. customized)", d: "Re-applies to EVERY user on this role. Discards individual customizations." },
  { v: "customized", l: "Customized users only", d: "Resets only individually customized users to match the template. Discards their customizations." },
];

// scopes/setScope: only relevant for approve/reject actions — a small
// "All / Centre-wise / Team" dropdown appears next to that checkbox once
// it's checked, letting admin pick who this specific approve or reject
// grant actually covers (see server/middleware/requirePermission.js's
// scopeAllowsEmployee/scopeAllowsCentre for how it's enforced).
const ActionCells = ({ node, pending, toggle, descendants = [], ancestors = [], scopes = {}, setScope }) => (
  <div className="flex items-center gap-[14px] flex-shrink-0">
    {ACTIONS.map((a) => {
      const id = node.actions[a];
      if (id === undefined) return <div key={a} className="w-[54px]" />;
      const checked = pending.has(id);
      const isScoped = a === "approve" || a === "reject";
      return (
        <div key={a} className="flex items-center gap-[6px]">
          <label className="flex items-center gap-[5px] w-[54px] cursor-pointer select-none">
            <input type="checkbox" checked={checked} onChange={() => toggle(id, a, descendants, ancestors)} className="cursor-pointer" />
            <span className="text-[11px] text-t3 font-semibold">{ACTION_LABELS[a]}</span>
          </label>
          {isScoped && checked && setScope && (
            <div className="w-[110px]">
              <Sl options={SCOPE_OPTIONS.map((o) => ({ v: o.v, l: o.l.split(" ")[0] }))} value={scopes[id] || "all"} onChange={(v) => setScope(id, v)} />
            </div>
          )}
        </div>
      );
    })}
  </div>
);

export const PermissionsManager = () => {
  const [mode, setMode] = useState("role"); // 'role' | 'user'
  const [selRole, setSelRole] = useState("hr_head");
  const [empOptions, setEmpOptions] = useState([]);
  const [selEmpId, setSelEmpId] = useState("");

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(() => new Set());
  // Modules collapsed by default — click one to open it, the rest stay
  // collapsed until clicked themselves (independent toggles, not a
  // forced single-open accordion).
  const [expandedModules, setExpandedModules] = useState(() => new Set());

  const [pending, setPending] = useState(new Set());
  // scope per approve/reject permission_id: 'all' | 'centre' | 'team'.
  // Only meaningful for approve/reject actions — ignored elsewhere. Missing
  // entries default to 'all'.
  const [scopes, setScopes] = useState({});
  // Snapshot of `pending`/`scopes` exactly as loaded, BEFORE any checkbox
  // click — never mutated by toggle()/setScope(). save() diffs `pending`
  // against this to find what the admin actually touched, instead of
  // sending the whole (possibly stale) `pending` set as absolute truth. See
  // save() for why: without this, a second admin's edit to the same
  // role/user made after this screen loaded gets silently clobbered by
  // this tab's full-replace Save.
  const [baseline, setBaseline] = useState({ ids: new Set(), scopes: {} });
  const [targetLoading, setTargetLoading] = useState(false);
  const [applyTarget, setApplyTarget] = useState("none");
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [customized, setCustomized] = useState(false);
  const [diff, setDiff] = useState({ added: [], removed: [] });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/api/permissions/catalog").then((d) => setCatalog(d?.catalog || [])).catch(() => setCatalog([])).finally(() => setCatalogLoading(false));
  }, []);

  useEffect(() => {
    api.get("/api/employees?status=Active&limit=999").then((e) => {
      const rows = Array.isArray(e) ? e : e.data || e.employees || [];
      setEmpOptions(rows.map((r) => ({ v: r.id, l: r.nm || r.name || r.id, sub: r.id })));
    }).catch(() => setEmpOptions([]));
  }, []);

  const loadTarget = () => {
    if (mode === "role") {
      setTargetLoading(true);
      api.get(`/api/permissions/roles/${selRole}`)
        .then((d) => {
          const ids = new Set(d?.permission_ids || []); const scp = d?.scopes || {};
          setPending(ids); setScopes(scp); setBaseline({ ids: new Set(ids), scopes: { ...scp } });
        })
        .catch(() => { setPending(new Set()); setScopes({}); setBaseline({ ids: new Set(), scopes: {} }); })
        .finally(() => setTargetLoading(false));
    } else if (mode === "user" && selEmpId) {
      setTargetLoading(true);
      api.get(`/api/permissions/users/${selEmpId}`)
        .then((d) => {
          const ids = new Set(d?.permission_ids || []); const scp = d?.scopes || {};
          setPending(ids); setScopes(scp); setCustomized(!!d?.customized); setDiff(d?.diff_from_role || { added: [], removed: [] });
          setBaseline({ ids: new Set(ids), scopes: { ...scp } });
        })
        .catch(() => { setPending(new Set()); setScopes({}); setCustomized(false); setDiff({ added: [], removed: [] }); setBaseline({ ids: new Set(), scopes: {} }); })
        .finally(() => setTargetLoading(false));
    }
  };
  useEffect(loadTarget, [mode, selRole, selEmpId]); // eslint-disable-line

  // What the admin actually clicked, independent of anything that may have
  // changed on the server since this screen loaded.
  const myAdded = () => [...pending].filter((id) => !baseline.ids.has(id));
  const myRemoved = () => [...baseline.ids].filter((id) => !pending.has(id));
  const myScopeChanges = () => { const out = {}; for (const id of pending) if (scopes[id] && scopes[id] !== baseline.scopes[id]) out[id] = scopes[id]; return out; };

  // Re-reads the server's CURRENT state and applies only the admin's own
  // added/removed toggles onto it — a three-way merge, not a blind
  // overwrite. If someone else changed this same role/user after this
  // screen loaded, their change survives; this admin's own clicks still
  // apply on top of it either way.
  const fetchFreshIdsAndScopes = async () => {
    const d = mode === "role"
      ? await api.get(`/api/permissions/roles/${selRole}`)
      : await api.get(`/api/permissions/users/${selEmpId}`);
    return { ids: new Set(d?.permission_ids || []), scopes: d?.scopes || {} };
  };

  const nodes = useMemo(() => pivot(catalog), [catalog]);
  const modules = useMemo(() => [...new Set(nodes.map((n) => n.module))].sort((a, b) => (a === "dashboard" ? -1 : a === "employees" ? (b === "dashboard" ? 1 : -1) : a.localeCompare(b))), [nodes]);

  const q = search.trim().toLowerCase();
  const matches = (n) => !q || fieldLabel(n).toLowerCase().includes(q) || n.module.toLowerCase().includes(q);

  const toggle = (id, action, descendants = [], ancestors = []) => setPending((prev) => {
    const next = new Set(prev);
    const willCheck = !next.has(id);
    const downIds = [id, ...descendants.map((n) => n.actions[action]).filter((x) => x !== undefined)];
    for (const aid of downIds) { if (willCheck) next.add(aid); else next.delete(aid); }
    // Checking on also lifts every ancestor's same action on — a child
    // can't be meaningfully "on" while its parent shows "off". Unchecking
    // never touches ancestors (a sibling might still need them).
    if (willCheck) {
      for (const anc of ancestors) { const aid = anc.actions[action]; if (aid !== undefined) next.add(aid); }
    }
    return next;
  });
  const setScope = (id, scope) => setScopes((prev) => ({ ...prev, [id]: scope }));
  const toggleExpanded = (key) => setExpanded((prev) => { const next = new Set(prev); next.has(key) ? next.delete(key) : next.add(key); return next; });
  const toggleExpandedModule = (mod) => setExpandedModules((prev) => { const next = new Set(prev); next.has(mod) ? next.delete(mod) : next.add(mod); return next; });

  const save = async () => {
    setSaving(true);
    try {
      const added = myAdded(), removed = myRemoved(), myScopes = myScopeChanges();
      const fresh = await fetchFreshIdsAndScopes();

      // Someone else's edit, landed between this screen loading and now —
      // tell the admin plainly rather than silently folding it in, since
      // this is exactly the kind of thing that used to look like "my change
      // didn't take" when it was actually someone else's change getting
      // clobbered (now: merged instead).
      const otherAdded = [...fresh.ids].filter((id) => !baseline.ids.has(id) && !added.includes(id));
      const otherRemoved = [...baseline.ids].filter((id) => !fresh.ids.has(id) && !removed.includes(id));
      if (otherAdded.length || otherRemoved.length) {
        notify(`Someone else changed this since you opened it (${otherAdded.length} added, ${otherRemoved.length} removed elsewhere). Your own ${added.length + removed.length} change(s) are applied on top of their current state, not over it.`, { title: "Merged with a newer change", variant: "info" });
      }

      const finalIds = new Set(fresh.ids);
      for (const id of added) finalIds.add(id);
      for (const id of removed) finalIds.delete(id);
      const finalScopes = { ...fresh.scopes, ...myScopes };

      if (mode === "role") {
        const d = await api.put(`/api/permissions/roles/${selRole}`, { permission_ids: [...finalIds], scopes: finalScopes });
        notify(`Saved ${ROLE_LABELS[selRole]}'s permissions — applied to ${d?.users_updated ?? 0} user(s) now.${d?.customized_received_new ? " People with individual permissions kept theirs and received what you newly added." : ""} They see it on their next page load.`, { title: "Saved", variant: "success" });
      } else {
        await api.put(`/api/permissions/users/${selEmpId}`, { permission_ids: [...finalIds], scopes: finalScopes });
        notify("Saved this employee's individual permissions", { title: "Saved", variant: "success" });
      }
      loadTarget();
      return true;
    } catch (e) { notify(e.message || "Failed to save permissions", { title: "Error", variant: "error" }); return false; }
    finally { setSaving(false); }
  };

  // Role-template mode: Save asks "apply to whom?" right here, in a dialog,
  // so there's no scrolling back down to a separate dropdown afterwards.
  const confirmSaveAndApply = async () => {
    const ok = await save();
    setApplyDialogOpen(false);
    if (!ok || applyTarget === "none") return;
    try {
      const d = await api.post(`/api/permissions/roles/${selRole}/apply-to-users`, { target: applyTarget });
      notify(`Applied to ${d?.count ?? 0} user(s)`, { title: "Done", variant: "success" });
    } catch (e) { notify(e.message || "Failed to apply to users", { title: "Error", variant: "error" }); }
  };

  const resetUser = async () => {
    if (!await confirmDialog("Reset this employee's permissions back to their role's current default? Their individual customizations will be discarded.", { title: "Reset to role default" })) return;
    try {
      await api.del(`/api/permissions/users/${selEmpId}/reset`);
      notify("Reset to role default", { title: "Done", variant: "success" });
      loadTarget();
    } catch (e) { notify(e.message || "Failed", { title: "Error", variant: "error" }); }
  };

  const renderModule = (mod) => {
    const modLevel = nodes.find((n) => n.module === mod && !n.tab && !n.field);
    // Not module-specific — any module can have flat leaf fields (tab=null,
    // field set: dashboard widgets, and the flat action modules like
    // helpdesk/documents/onboarding) and/or real tabs with their own child
    // fields (employees, and every other tabbed module: assets, separation,
    // attendance, leaves, appraisal, payroll, expenses, recruitment,
    // compliance, myprofile, org).
    const widgets = nodes.filter((n) => n.module === mod && !n.tab && n.field);
    const tabs = nodes.filter((n) => n.module === mod && n.tab && !n.field);

    const visibleWidgets = widgets.filter(matches);
    const visibleTabs = tabs.filter((t) => matches(t) || nodes.some((n) => n.module === mod && n.tab === t.tab && n.field && matches(n)));
    if (q && !matches(modLevel || { module: mod, label: mod }) && !visibleWidgets.length && !visibleTabs.length) return null;

    const hasChildren = visibleWidgets.length > 0 || visibleTabs.length > 0;
    // Auto-open while searching, so a match isn't hidden inside a collapsed module.
    const isModuleOpen = hasChildren && (expandedModules.has(mod) || !!q);
    // Every other node under this module — toggling the module checkbox cascades to all of them.
    const moduleDescendants = nodes.filter((n) => n.module === mod && n !== modLevel);

    return (
      <Cd key={mod} style={{ marginBottom: 10 }}>
        <div className="flex items-center justify-between flex-wrap gap-2 mb-1 py-[4px] px-[8px] -mx-[8px] rounded-[6px] hover:bg-bg2 transition-colors duration-100">
          <div className="flex items-center">
          <button
            onClick={() => hasChildren && toggleExpandedModule(mod)}
            className="flex items-center gap-1 bg-transparent border-0 p-0 text-[14px] font-bold text-t0"
            style={{ cursor: hasChildren ? "pointer" : "default" }}
          >
            {hasChildren ? (isModuleOpen ? <ChevronDown size={14} /> : <ChevronRight size={14} />) : <span className="w-[14px]" />}
            {MODULE_LABELS[mod] || mod}
          </button>
          {modLevel && <PermInfo node={modLevel} />}
          </div>
          {modLevel && <ActionCells node={modLevel} pending={pending} toggle={toggle} descendants={moduleDescendants} scopes={scopes} setScope={setScope} />}
        </div>

        {isModuleOpen && visibleWidgets.length > 0 && (
          <div className="mt-2 pl-3 border-l-2 border-b0">
            <div className="text-[11px] text-t3 uppercase font-bold mb-1">{mod === "dashboard" ? "Widgets" : "Actions"}</div>
            {visibleWidgets.map((n) => (
              <div key={n.field} className="flex items-center justify-between py-[5px] gap-2 px-[8px] -mx-[8px] rounded-[6px] hover:bg-bg2 transition-colors duration-100">
                <div className="text-[14px] text-t1 truncate flex items-center min-w-0"><span className="truncate">{fieldLabel(n)}</span><PermInfo node={n} /></div>
                <ActionCells node={n} pending={pending} toggle={toggle} ancestors={modLevel ? [modLevel] : []} scopes={scopes} setScope={setScope} />
              </div>
            ))}
          </div>
        )}

        {isModuleOpen && visibleTabs.length > 0 && (
          <div className="mt-2">
            {visibleTabs.map((t) => {
              const key = `${mod}|${t.tab}`;
              const allFields = nodes.filter((n) => n.module === mod && n.tab === t.tab && n.field);
              const fields = allFields.filter(matches);
              const isOpen = expanded.has(key) || (q && fields.length > 0);
              return (
                <div key={t.tab} className="border-t border-b0 pt-1 mt-1">
                  <div className="flex items-center justify-between gap-2 py-[3px] px-[8px] -mx-[8px] rounded-[6px] hover:bg-bg2 transition-colors duration-100">
<div className="flex items-center">
                    <button onClick={() => toggleExpanded(key)} className="flex items-center gap-1 bg-transparent border-0 cursor-pointer text-[14px] font-semibold text-t0 p-0">
                      {fields.length > 0 ? (isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />) : <span className="w-[13px]" />}
                      {fieldLabel(t)}
                    </button>
                    <PermInfo node={t} />
                    </div>
                    <ActionCells node={t} pending={pending} toggle={toggle} descendants={allFields} ancestors={modLevel ? [modLevel] : []} scopes={scopes} setScope={setScope} />
                  </div>
                  {isOpen && fields.length > 0 && (
                    <div className="pl-5 border-l-2 border-b0 ml-1">
                      {fields.map((n) => (
                        <div key={n.field} className="flex items-center justify-between py-[4px] gap-2 px-[8px] -mx-[8px] rounded-[6px] hover:bg-bg2 transition-colors duration-100">
                          <div className="text-[13px] text-t2 truncate flex items-center min-w-0"><span className="truncate">{fieldLabel(n)}</span><PermInfo node={n} /></div>
                          <ActionCells node={n} pending={pending} toggle={toggle} ancestors={[t, ...(modLevel ? [modLevel] : [])]} scopes={scopes} setScope={setScope} />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Cd>
    );
  };

  return (
    <div>
      <div className="text-[14px] font-bold text-t0 mb-1">Granular Permissions</div>
      <div className="text-[14px] text-t3 mb-[14px]">Every module, tab and field, each with independent View / Edit / Delete. Admin always has full access. Assigning a role copies its template onto that user — individual permissions can then be added or removed without affecting the role or anyone else.</div>

      <div className="flex items-center gap-2 mb-3">
        <Bt v={mode === "role" ? "primary" : "secondary"} s="sm" onClick={() => setMode("role")}>Role Template</Bt>
        <Bt v={mode === "user" ? "primary" : "secondary"} s="sm" onClick={() => setMode("user")}>Individual User</Bt>
      </div>

      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {mode === "role" ? (
          <div className="w-[220px]">
            <Sl options={EDITABLE_ROLES.map((r) => ({ v: r, l: ROLE_LABELS[r] }))} value={selRole} onChange={setSelRole} />
          </div>
        ) : (
          <div className="w-[280px]">
            <SearchSelect options={empOptions} value={selEmpId} onChange={setSelEmpId} placeholder="Search an employee…" searchPlaceholder="Name or ID…" clearable />
          </div>
        )}
        <div className="flex-1 min-w-[200px] flex items-center gap-1 px-[10px] py-[6px] bg-bg2 border border-b0 rounded-[10px]">
          <Search size={13} className="text-t3" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search permissions…" className="flex-1 bg-transparent outline-none text-[14px] text-t0" />
        </div>
        {mode === "user" && selEmpId && customized && (
          <Bt v="ghost" s="sm" icon={RotateCcw} onClick={resetUser}>Reset to role default</Bt>
        )}
      </div>

      {mode === "user" && selEmpId && customized && (
        <div className="text-[12px] text-t3 mb-2">Customized — {diff.added.length} added, {diff.removed.length} removed vs. role default.</div>
      )}

      {catalogLoading ? (
        <div className="text-[14px] text-t3 py-6 text-center">Loading catalog…</div>
      ) : mode === "user" && !selEmpId ? (
        <div className="text-[14px] text-t3 py-6 text-center">Search for an employee above to edit their individual permissions.</div>
      ) : targetLoading ? (
        <div className="text-[14px] text-t3 py-6 text-center">Loading…</div>
      ) : (
        <>
          {modules.map(renderModule)}
          <div className="flex items-center gap-2 flex-wrap">
            <Bt v="primary" onClick={mode === "role" ? () => setApplyDialogOpen(true) : save} disabled={saving} icon={Check}>{saving ? "Saving…" : "Save Changes"}</Bt>
          </div>
        </>
      )}
      {applyDialogOpen && (
        <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4" onClick={() => !saving && setApplyDialogOpen(false)}>
          <div className="bg-bg1 rounded-[14px] shadow-xl w-full max-w-[460px] p-5" onClick={(e) => e.stopPropagation()}>
            <div className="text-[16px] font-bold text-t0 mb-1">Save {ROLE_LABELS[selRole]} template</div>
            <div className="text-[13px] text-t3 mb-3">Choose who this template should be applied to.</div>
            <div className="flex flex-col gap-2 mb-4">
              {APPLY_TARGET_OPTIONS.map((o) => (
                <label key={o.v} className={`flex items-start gap-2 p-[10px] rounded-[10px] border cursor-pointer ${applyTarget === o.v ? "border-p bg-pl/10" : "border-b0"}`}>
                  <input type="radio" name="apply-target" className="mt-[3px]" checked={applyTarget === o.v} onChange={() => setApplyTarget(o.v)} />
                  <span>
                    <span className="block text-[13px] font-semibold text-t0">{o.l}</span>
                    <span className="block text-[12px] text-t3">{o.d}</span>
                  </span>
                </label>
              ))}
            </div>
            <div className="flex justify-end gap-2">
              <Bt v="ghost" onClick={() => setApplyDialogOpen(false)} disabled={saving}>Cancel</Bt>
              <Bt v="primary" onClick={confirmSaveAndApply} disabled={saving} icon={Check}>{saving ? "Saving…" : "Save"}</Bt>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
